import httpClient, { httpClientWithoutInterceptors } from '@/utils/httpClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { UserUpdateForm, UserUpdatePasswordForm } from '@/lib/schema/v1/user'
import { logSchema } from '@/lib/schema/v1/log'
import { toast } from 'vue-sonner'
import enAu from 'dayjs/locale/en-au'
import { Locale } from '@/lib/locale'
import dayjs from 'dayjs'
import { RetrieveByUuidParams } from '@/lib/types/v1'
import { ref, toValue, watchEffect } from 'vue'
import { queryKeys } from '@/lib/query-keys/v1'
import { isArray, isBoolean, isNumber, parseAmpResponse } from '@/lib/utils'
import { RetrieveUserResponse, retrieveUserResponseSchema } from '@/lib/schema/v2/user'

export const useUserQuery = (params: RetrieveByUuidParams | (() => RetrieveByUuidParams)) => {
    const staleTime = ref(0)
    const enabled = ref(true)
    const refetchOnWindowFocus = ref(true)
    const keys = ref(queryKeys.user.key())

    watchEffect(() => {
        const value: RetrieveByUuidParams | undefined = toValue(params)

        if (isNumber(value?.staleTime)) {
            staleTime.value = value.staleTime
        }

        if (isBoolean(value?.enabled)) {
            enabled.value = value.enabled
        }

        if (isBoolean(value?.refetchOnWindowFocus)) {
            refetchOnWindowFocus.value = value.refetchOnWindowFocus
        }

        if (isArray(value?.queryKeys)) {
            keys.value = value.queryKeys
        }
    })

    return useQuery({
        queryKey: keys,
        queryFn: async () => {
            const { data } = await httpClientWithoutInterceptors.get('v1/user')

            return parseAmpResponse(data, retrieveUserResponseSchema) as RetrieveUserResponse
        },
        select: (data) => {
            if (!data) {
                return null
            }

            const parsed = parseAmpResponse(
                data,
                retrieveUserResponseSchema
            ) as RetrieveUserResponse

            if (parsed.data?.locale === Locale.EnAu) {
                dayjs.locale(enAu)
            }

            return parsed.data
        },
        enabled: enabled,
        staleTime: staleTime,
        refetchOnWindowFocus: refetchOnWindowFocus,
    })
}

export const useUpdateUserMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (form: UserUpdateForm) => {
            const { data } = await httpClient.patch('v1/user', form)

            return logSchema.parse(data.data)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: queryKeys.user.key(),
            })

            toast.success('Updated successfully.', {
                position: 'bottom-right',
                closeButton: false,
            })
        },
    })
}

export const useUpdateUserPasswordMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (form: UserUpdatePasswordForm) => {
            const { data } = await httpClient.patch('v1/user/password', form)

            return logSchema.parse(data.data)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: queryKeys.user.key(),
            })

            toast.success('Password updated successfully.', {
                position: 'bottom-right',
                closeButton: false,
            })
        },
    })
}

export const useEnableUserNotificationMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (uuid: string) => {
            const { data } = await httpClient.patch(`v1/user/settings/notifications/${uuid}/enable`)

            return logSchema.parse(data.data)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: queryKeys.user.key(),
            })

            toast.success('Notification is now enabled.', {
                position: 'bottom-right',
                closeButton: false,
            })
        },
    })
}

export const useDisableUserNotificationMutation = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (uuid: string) => {
            const { data } = await httpClient.patch(
                `v1/user/settings/notifications/${uuid}/disable`
            )

            return logSchema.parse(data.data)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: queryKeys.user.key(),
            })

            toast.success('Notification is now disabled.', {
                position: 'bottom-right',
                closeButton: false,
            })
        },
    })
}
